import React, { useEffect, useMemo, useState } from 'react';
import Modal, { ModalFooter } from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useI18n } from '../../context/I18nContext';
import { accountService, DeleteAccountReason } from '../../services/account';
import { AlertTriangle } from 'lucide-react';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

const REASON_KEYS: DeleteAccountReason[] = [
  'too_expensive',
  'missing_features',
  'too_complex',
  'switched_tool',
  'not_using',
  'other',
];

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  onDeleted,
}) => {
  const { t, language } = useI18n();
  const [reason, setReason] = useState<DeleteAccountReason | ''>('');
  const [comment, setComment] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expectedConfirm = language === 'fr' ? 'SUPPRIMER' : 'DELETE';

  const reasonOptions = useMemo(
    () =>
      REASON_KEYS.map((key) => ({
        value: key,
        label: t(`settings.deleteAccountReasons.${key}`),
      })),
    [t]
  );

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setComment('');
      setConfirmText('');
      setIsDeleting(false);
      setError(null);
    }
  }, [isOpen]);

  const canSubmit =
    confirmText.trim().toUpperCase() === expectedConfirm && !isDeleting;

  const handleDelete = async () => {
    if (!canSubmit) return;
    setIsDeleting(true);
    setError(null);

    try {
      await accountService.deleteAccount({
        reason: reason || undefined,
        comment: comment.trim() || undefined,
      });
      onDeleted();
    } catch (err) {
      console.error('Account deletion failed:', err);
      setError(t('settings.deleteAccountError'));
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isDeleting ? () => undefined : onClose}
      title={t('settings.deleteAccountTitle')}
      size="md"
      showCloseButton={!isDeleting}
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onClose}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="danger"
            size="md"
            onClick={handleDelete}
            loading={isDeleting}
            disabled={!canSubmit}
          >
            {isDeleting
              ? t('settings.deleteAccountDeleting')
              : t('settings.deleteAccountConfirm')}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50/80 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
          <div className="space-y-2 text-sm text-red-900">
            <p className="font-medium">{t('settings.deleteAccountWarningTitle')}</p>
            <p className="text-red-800/90">{t('settings.deleteAccountWarningBody')}</p>
          </div>
        </div>

        <Select
          label={`${t('settings.deleteAccountReasonLabel')} (${t('settings.deleteAccountOptional')})`}
          value={reason}
          onChange={(value) => setReason(value as DeleteAccountReason | '')}
          options={reasonOptions}
          placeholder={t('settings.deleteAccountReasonPlaceholder')}
          disabled={isDeleting}
          showEmptyOptionInList={false}
        />

        <div>
          <label
            htmlFor="delete-account-comment"
            className="mb-1.5 block text-sm font-medium text-gray-700"
          >
            {t('settings.deleteAccountCommentLabel')}{' '}
            <span className="font-normal text-gray-400">
              ({t('settings.deleteAccountOptional')})
            </span>
          </label>
          <textarea
            id="delete-account-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            disabled={isDeleting}
            rows={3}
            placeholder={t('settings.deleteAccountCommentPlaceholder')}
            className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-base sm:text-sm text-gray-900 shadow-sm hover:border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-60"
          />
        </div>

        <div>
          <p className="mb-1.5 text-sm text-gray-700">
            {t('settings.deleteAccountConfirmPrompt', { word: expectedConfirm })}
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={isDeleting}
            autoComplete="off"
            placeholder={expectedConfirm}
            className="font-mono tracking-wide"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default DeleteAccountModal;
